import { EditableBeatsView } from "@softmax/product-ux/editor";
import { firstSentence } from "@softmax/product-ux/catalog";
import type { PlayBeat } from "../store-page/types";
import { useEditModeMaybe } from "../use-edit-mode-maybe";
import {
  beatBodyMaxCharacters,
  beatTitleMaxCharacters,
  maxBeats,
  minBeats,
} from "../store-page/content-limits";

export function EditableBeatsList({
  liveBeats,
}: {
  liveBeats: readonly PlayBeat[];
}) {
  const mode = useEditModeMaybe();

  if (!mode?.editing)
    return (
      <EditableBeatsView
        mode="read"
        beats={liveBeats.map((beat) => ({
          ...beat,
          body: firstSentence(beat.body),
        }))}
      />
    );

  const { editor } = mode;
  // Preserve the live template until the creator makes their first change.
  const beats =
    editor.content.play_beats ?? liveBeats.map((beat) => ({ ...beat }));
  return (
    <EditableBeatsView
      mode="edit"
      loading={!editor.loaded}
      minBeats={minBeats}
      maxBeats={maxBeats}
      maxTitleLength={beatTitleMaxCharacters}
      maxBodyLength={beatBodyMaxCharacters}
      beats={beats.map((beat, index) => ({
        ...beat,
        titleWarnings: editor.lintWarnings.filter(
          (warning) => warning.field === `play_beats[${index}].title`,
        ),
        bodyWarnings: editor.lintWarnings.filter(
          (warning) => warning.field === `play_beats[${index}].body`,
        ),
      }))}
      fitPreview={firstSentence}
      onUpdate={({ index, patch }) =>
        editor.updateContent({
          play_beats: beats.map((beat, i) =>
            i === index ? { ...beat, ...patch } : beat,
          ),
        })
      }
      onAdd={() => {
        if (beats.length < maxBeats)
          editor.updateContent({
            play_beats: [...beats, { title: "", body: "" }],
          });
      }}
      onRemove={(index) =>
        editor.updateContent({
          play_beats: beats.filter((_, i) => i !== index),
        })
      }
    />
  );
}
